import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ListOrdered, Package, Play, RotateCcw, Square } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

import { type KittingCartSlot } from "./kittingCartModel";
import { KittingInventoryDiscrepancyButton } from "./KittingInventoryDiscrepancyButton";
import { KittingOrderSelectModal } from "./KittingOrderSelectModal";
import { KittingCartSlotHeadline } from "./kittingPageEntityLinks";
import {
  kittingBatchPeekContext,
  type KittingBatchPeekMap,
} from "./kittingBatchPeek";
import {
  assignKittingCartMarks,
  kittingCartMarkForSlot,
  kittingCartMarksFromRunRecord,
} from "./kittingCartMarks";
import {
  fetchKittingPickData,
  kittingPickDataQueryKey,
  KITTING_PICK_DATA_QUERY_STALE_MS,
} from "./kittingPickDataApi";
import {
  coLocatedCartSlots,
} from "./kittingOrderSelectionModel";
import { KITTING_LABOR_FLASH_STORAGE_KEY } from "./kittingLaborFlash";
import { useKittingPageData } from "./useKittingPageData";
import {
  buildKittingPickPageRunSearchParams,
  cancelKittingPickRun,
  createKittingPickRun,
  fetchCurrentKittingPickRun,
  finishKittingPickRun,
  kittingCurrentPickRunQueryKey,
  type KittingPickRun,
} from "./kittingPickRunApi";
import {
  applyKittingPickCancelResponse,
  kittingRunActiveCartLineCount,
  kittingRunTotalPickedQuantity,
} from "./kittingPickActionCache";

const KITTING_PAGE_HELP =
  "Tap a job to start picking, then scan component SKUs on the pick screen. Station prestock and print-on-demand labels are excluded from picks.";

export function KittingPage() {
  const canWrite = useHubModuleWrite("manufacturing");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [orderSelectAnchor, setOrderSelectAnchor] = useState<KittingCartSlot | null>(null);
  const [laborReturnFlash, setLaborReturnFlash] = useState<string | null>(null);
  const [finishConfirmRun, setFinishConfirmRun] = useState<KittingPickRun | null>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(KITTING_LABOR_FLASH_STORAGE_KEY);
      if (raw != null && raw.trim() !== "") {
        sessionStorage.removeItem(KITTING_LABOR_FLASH_STORAGE_KEY);
        setLaborReturnFlash(raw.trim());
      }
    } catch {
      /* private mode or storage unavailable */
    }
  }, []);

  const openRunQuery = useQuery({
    queryKey: kittingCurrentPickRunQueryKey(),
    queryFn: fetchCurrentKittingPickRun,
    refetchOnWindowFocus: false,
  });

  const {
    cartSlots,
    pickLines,
    batchPeeksById,
    batchPeekLoading,
    incomingLoading,
    incomingError,
  } = useKittingPageData();

  const openRunSlotSet = useMemo(() => {
    const slots = openRunQuery.data?.slotKeys ?? [];
    return new Set(slots);
  }, [openRunQuery.data?.slotKeys]);

  const queueSlots = useMemo(
    () => cartSlots.filter((slot) => !openRunSlotSet.has(slot.slotKey)),
    [cartSlots, openRunSlotSet],
  );

  const coLocatedForAnchor = useMemo(() => {
    if (orderSelectAnchor == null) return [];
    return coLocatedCartSlots({
      anchorSlotId: orderSelectAnchor.slotKey,
      pickLines,
      cartSlots: queueSlots,
    });
  }, [orderSelectAnchor, pickLines, queueSlots]);

  const heroSlot = queueSlots[0] ?? null;
  const laterSlots = heroSlot != null ? queueSlots.slice(1) : [];

  const startPickMutation = useMutation({
    mutationFn: async (slotIds: string[]) => {
      const marks = assignKittingCartMarks(slotIds);
      const cartMarks: Record<string, string> = {};
      for (const mark of marks.values()) {
        cartMarks[mark.slotKey] = mark.letter;
      }
      return createKittingPickRun({ slotKeys: slotIds, cartMarks });
    },
    onSuccess: async (run) => {
      await queryClient.invalidateQueries({ queryKey: kittingCurrentPickRunQueryKey() });
      void queryClient.prefetchQuery({
        queryKey: kittingPickDataQueryKey(run.slotKeys),
        queryFn: () => fetchKittingPickData(run.slotKeys),
        staleTime: KITTING_PICK_DATA_QUERY_STALE_MS,
      });
      const params = buildKittingPickPageRunSearchParams(run.id);
      navigate(`/manufacturing/kitting/pick?${params.toString()}`);
      setOrderSelectAnchor(null);
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.status === 409) {
        const body = error.body as { existingRun?: KittingPickRun } | undefined;
        if (body?.existingRun != null) {
          showToast({
            message: "You already have an open pick run. Resume it from the card above.",
            tone: "error",
          });
          void queryClient.invalidateQueries({ queryKey: kittingCurrentPickRunQueryKey() });
          setOrderSelectAnchor(null);
          return;
        }
      }
      const message = error instanceof ApiError ? error.message : "Could not start pick run.";
      showToast({ message, tone: "error" });
    },
  });

  const finishRunMutation = useMutation({
    mutationFn: async (input: { runId: number; confirmCartNotEmpty: boolean }) =>
      finishKittingPickRun(input.runId, { confirmCartNotEmpty: input.confirmCartNotEmpty }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: kittingCurrentPickRunQueryKey() });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "kitting", "pick-data"] });
      setFinishConfirmRun(null);
      showToast({ message: "Pick run finished.", tone: "success" });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.status === 409) {
        const body = error.body as { warning?: string; run?: KittingPickRun } | undefined;
        if (body?.warning === "cart_has_quantities" && body.run != null) {
          setFinishConfirmRun(body.run);
          return;
        }
      }
      const message = error instanceof ApiError ? error.message : "Could not finish pick run.";
      showToast({ message, tone: "error" });
    },
  });

  const cancelRunMutation = useMutation({
    mutationFn: async (input: { runId: number; pickedTotal: number }) => ({
      response: await cancelKittingPickRun(input.runId),
      pickedTotal: input.pickedTotal,
    }),
    onSuccess: ({ response, pickedTotal }) => {
      applyKittingPickCancelResponse(queryClient, response);
      showToast({
        message:
          pickedTotal > 0
            ? "Pick run cancelled and all cart quantities returned."
            : "Pick run cancelled.",
        tone: "success",
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof ApiError ? error.message : "Could not cancel pick run.";
      showToast({ message, tone: "error" });
    },
  });

  const startPick = (slotIds: string[]): void => {
    if (!canWrite || startPickMutation.isPending) return;
    startPickMutation.mutate(slotIds);
  };

  const resumeOpenRun = (): void => {
    const run = openRunQuery.data;
    if (run == null) return;
    const params = buildKittingPickPageRunSearchParams(run.id);
    navigate(`/manufacturing/kitting/pick?${params.toString()}`);
  };

  const requestFinishOpenRun = (): void => {
    const run = openRunQuery.data;
    if (run == null || finishRunMutation.isPending) return;
    finishRunMutation.mutate({ runId: run.id, confirmCartNotEmpty: false });
  };

  const confirmFinishOpenRun = (): void => {
    if (finishConfirmRun == null || finishRunMutation.isPending) return;
    finishRunMutation.mutate({ runId: finishConfirmRun.id, confirmCartNotEmpty: true });
  };

  const requestCancelOpenRun = (): void => {
    const run = openRunQuery.data;
    if (run == null || cancelRunMutation.isPending) return;
    cancelRunMutation.mutate({
      runId: run.id,
      pickedTotal: kittingRunTotalPickedQuantity(run),
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Package className="h-6 w-6 text-brand-600" aria-hidden />
            <h1 className="text-xl font-semibold text-slate-900">Picking</h1>
            <HoverHintWithGlyph hoverHint={KITTING_PAGE_HELP} size="md" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <KittingInventoryDiscrepancyButton />
        </div>
      </header>

      {laborReturnFlash ? (
        <div
          className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-900"
          role="status"
        >
          <p className="min-w-0 flex-1 leading-snug">{laborReturnFlash}</p>
          <Button size="sm" variant="secondary" type="button" onClick={() => setLaborReturnFlash(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}

      {openRunQuery.data != null ? (
        <Card>
          <CardBody>
            <KittingOpenRunCard
              run={openRunQuery.data}
              cartSlots={cartSlots}
              disabled={!canWrite}
              finishing={finishRunMutation.isPending}
              cancelling={cancelRunMutation.isPending}
              onResume={resumeOpenRun}
              onFinish={requestFinishOpenRun}
              onCancel={requestCancelOpenRun}
            />
          </CardBody>
        </Card>
      ) : null}

      {finishConfirmRun != null ? (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-4 text-sm text-amber-950"
          role="alertdialog"
          aria-labelledby="kitting-finish-confirm-heading"
        >
          <h2 id="kitting-finish-confirm-heading" className="font-semibold">
            Finish pick run with items still in cart?
          </h2>
          <p className="mt-2 leading-snug">
            This run still has picked quantities in the cart. Work order allocation is already recorded in
            Supabase; finishing only clears the cart session.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="primary"
              type="button"
              disabled={finishRunMutation.isPending}
              onClick={confirmFinishOpenRun}
            >
              Finish anyway
            </Button>
            <Button
              size="sm"
              variant="secondary"
              type="button"
              disabled={finishRunMutation.isPending}
              onClick={() => setFinishConfirmRun(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <Card>
        <CardBody>
          {incomingLoading ? (
            <p className="py-6 text-center text-sm text-slate-500">Loading jobs…</p>
          ) : incomingError != null ? (
            <p className="py-6 text-center text-sm text-rose-700">{incomingError.message}</p>
          ) : heroSlot == null ? (
            <p className="py-6 text-center text-sm text-slate-600">
              {openRunQuery.data != null ? "No other jobs in queue." : "No active jobs to pick."}
            </p>
          ) : (
            <div className="space-y-6">
              <section aria-labelledby="kitting-next-heading">
                <h2 id="kitting-next-heading" className="sr-only">
                  Next job
                </h2>
                <KittingStartPickHero
                  slot={heroSlot}
                  disabled={!canWrite || startPickMutation.isPending}
                  batchPeeksById={batchPeeksById}
                  batchPeekLoading={batchPeekLoading}
                  onOpen={() => setOrderSelectAnchor(heroSlot)}
                />
              </section>

              {laterSlots.length > 0 ? (
                <section aria-labelledby="kitting-after-heading" className="space-y-2">
                  <h2 id="kitting-after-heading" className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <ListOrdered className="h-4 w-4 text-slate-500" aria-hidden />
                    Rest of queue
                  </h2>
                  <ol className="list-none space-y-2 pl-0">
                    {laterSlots.map((slot, index) => (
                      <KittingStartPickLaterRow
                        key={slot.slotKey}
                        stepNumber={index + 2}
                        slot={slot}
                        disabled={!canWrite || startPickMutation.isPending}
                        batchPeeksById={batchPeeksById}
                        batchPeekLoading={batchPeekLoading}
                        onOpen={() => setOrderSelectAnchor(slot)}
                      />
                    ))}
                  </ol>
                </section>
              ) : null}
            </div>
          )}
        </CardBody>
      </Card>

      <KittingOrderSelectModal
        open={orderSelectAnchor != null}
        anchorSlot={orderSelectAnchor}
        coLocatedSlots={coLocatedForAnchor}
        cartSlots={queueSlots}
        pickLines={pickLines}
        onClose={() => setOrderSelectAnchor(null)}
        onConfirm={startPick}
      />

    </div>
  );
}

function KittingOpenRunCard({
  run,
  cartSlots,
  disabled,
  finishing,
  cancelling,
  onResume,
  onFinish,
  onCancel,
}: {
  run: KittingPickRun;
  cartSlots: readonly KittingCartSlot[];
  disabled: boolean;
  finishing: boolean;
  cancelling: boolean;
  onResume: () => void;
  onFinish: () => void;
  onCancel: () => void;
}) {
  const slotById = useMemo(() => new Map(cartSlots.map((slot) => [slot.slotKey, slot])), [cartSlots]);
  const marks = useMemo(
    () => kittingCartMarksFromRunRecord(run.slotKeys, run.cartMarks),
    [run.slotKeys, run.cartMarks],
  );
  const totalPicked = kittingRunTotalPickedQuantity(run);
  const cartLineCount = kittingRunActiveCartLineCount(run);

  return (
    <section aria-labelledby="kitting-open-run-heading" className="space-y-3">
      <h2 id="kitting-open-run-heading" className="text-sm font-semibold text-slate-800">
        Your open pick run
      </h2>
      <div className="rounded-lg border-2 border-brand-500 bg-brand-50/30 p-4">
        <p className="text-sm text-slate-700">
          <span className="font-semibold text-slate-900">{totalPicked}</span> units picked
          {cartLineCount > 0 ? (
            <>
              {" "}
              across <span className="font-semibold text-slate-900">{cartLineCount}</span> cart line
              {cartLineCount === 1 ? "" : "s"}
            </>
          ) : (
            " (cart empty)"
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          {run.slotKeys.map((slotKey) => {
            const mark = kittingCartMarkForSlot(marks, slotKey);
            const slot = slotById.get(slotKey);
            return (
              <span
                key={slotKey}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium",
                  mark?.chipClass ?? "border-slate-300 bg-white text-slate-800",
                )}
              >
                {mark != null ? (
                  <span className={cn("inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold", mark.badgeClass)}>
                    {mark.letter}
                  </span>
                ) : null}
                <span className="truncate">{slot?.headlineLabel ?? slotKey}</span>
              </span>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" variant="primary" type="button" disabled={disabled || cancelling} onClick={onResume}>
            <Play className="mr-1 h-4 w-4" aria-hidden />
            Resume
          </Button>
          <Button
            size="sm"
            variant="secondary"
            type="button"
            disabled={disabled || finishing || cancelling}
            onClick={onFinish}
          >
            <Square className="mr-1 h-4 w-4" aria-hidden />
            Finish run
          </Button>
          <Button
            size="sm"
            variant="danger"
            type="button"
            disabled={disabled || finishing || cancelling}
            onClick={onCancel}
          >
            <RotateCcw className="mr-1 h-4 w-4" aria-hidden />
            {totalPicked > 0 ? "Cancel and return all" : "Cancel run"}
          </Button>
        </div>
      </div>
    </section>
  );
}

function kittingStartPickBatchPeek(
  slot: KittingCartSlot,
  batchPeeksById: KittingBatchPeekMap,
  batchPeekLoading: boolean,
): ReturnType<typeof kittingBatchPeekContext> | undefined {
  if (slot.kind !== "batch" || slot.batchIds[0] == null) return undefined;
  return kittingBatchPeekContext({
    apiPeek: batchPeeksById[slot.batchIds[0]!],
    linkedWorkOrderNumbers: slot.linkedWorkOrderNumbers,
    loading: batchPeekLoading,
  });
}

function KittingStartPickHero({
  slot,
  disabled,
  batchPeeksById,
  batchPeekLoading,
  onOpen,
}: {
  slot: KittingCartSlot;
  disabled: boolean;
  batchPeeksById: KittingBatchPeekMap;
  batchPeekLoading: boolean;
  onOpen: () => void;
}) {
  const batchPeek = kittingStartPickBatchPeek(slot, batchPeeksById, batchPeekLoading);

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onOpen}
      className={cn(
        "w-full rounded-lg border-2 border-brand-500 bg-brand-50/40 p-4 text-left shadow-md ring-2 ring-brand-500/20",
        "transition-colors hover:bg-brand-50/70",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <KittingCartSlotHeadline
            slot={slot}
            batchPeek={batchPeek}
            batchPeekLoading={batchPeekLoading}
            variant="hero"
            stopCardNavigation
          />
          <p className="text-sm text-slate-700">
            <span className="font-medium">{slot.openLineCount}</span> open line
            {slot.openLineCount === 1 ? "" : "s"}
            {slot.dueHint != null ? (
              <>
                {" "}
                · <span className="font-medium">{slot.dueHint}</span>
              </>
            ) : null}
          </p>
        </div>
        <span
          className="pointer-events-none inline-flex h-10 shrink-0 items-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-medium text-white"
          aria-hidden
        >
          Open
          <ChevronRight className="h-4 w-4" />
        </span>
      </div>
    </button>
  );
}

function KittingStartPickLaterRow({
  stepNumber,
  slot,
  disabled,
  batchPeeksById,
  batchPeekLoading,
  onOpen,
}: {
  stepNumber: number;
  slot: KittingCartSlot;
  disabled: boolean;
  batchPeeksById: KittingBatchPeekMap;
  batchPeekLoading: boolean;
  onOpen: () => void;
}) {
  const batchPeek = kittingStartPickBatchPeek(slot, batchPeeksById, batchPeekLoading);

  return (
    <li>
      <button
        type="button"
        disabled={disabled}
        onClick={onOpen}
        className={cn(
          "flex w-full flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left shadow-sm",
          "transition-colors hover:border-slate-300 hover:bg-slate-50",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-700">
            {stepNumber}
          </span>
          <div className="min-w-0 flex-1 space-y-0.5">
            <KittingCartSlotHeadline
              slot={slot}
              batchPeek={batchPeek}
              batchPeekLoading={batchPeekLoading}
              stopCardNavigation
            />
            <p className="text-xs text-slate-500">
              <span className="font-medium">{slot.openLineCount}</span> open line
              {slot.openLineCount === 1 ? "" : "s"}
              {slot.dueHint != null ? ` · ${slot.dueHint}` : ""}
            </p>
          </div>
        </div>
        <span className="pointer-events-none inline-flex items-center gap-1 text-sm font-medium text-brand-700" aria-hidden>
          Open
          <ChevronRight className="h-4 w-4" />
        </span>
      </button>
    </li>
  );
}
